import type { health as healthContracts } from '@personal-blog/contracts';

const API_BASE =
  typeof import.meta.env.VITE_API_URL === 'string' ? import.meta.env.VITE_API_URL : '';
export const CSRF_COOKIE_NAME: string =
  typeof import.meta.env.VITE_CSRF_COOKIE_NAME === 'string'
    ? import.meta.env.VITE_CSRF_COOKIE_NAME
    : 'pb_csrf';
const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

export class ApiRequestError extends Error implements ApiError {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Build headers via the Headers constructor so we accept any of HeadersInit's
  // three shapes (Headers / Record / [string, string][]) without relying on
  // an unsafe object spread.
  const headers = new Headers(init?.headers);
  // Only declare a JSON content-type when we're actually sending a body.
  // Fastify's content-type-parser rejects requests where the header says
  // `application/json` but the body is empty — "Body cannot be empty when
  // content-type is set to 'application/json'" — which crashes every
  // bodyless POST (logout, group apply, archive DELETE, ...). The header
  // doesn't apply to GETs anyway, so omitting it for body-less requests
  // is the right default.
  const body = init?.body;
  const hasBody = body !== undefined && body !== null;
  const shouldDefaultJson =
    hasBody &&
    typeof body === 'string' &&
    !headers.has('Content-Type');
  if (shouldDefaultJson) {
    headers.set('Content-Type', 'application/json');
  }
  const method = (init?.method ?? 'GET').toUpperCase();
  if (STATE_CHANGING.has(method) && !headers.has('X-CSRF-Token')) {
    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (csrfToken !== null) headers.set('X-CSRF-Token', csrfToken);
  }

  const res = await fetch(apiUrl(path), {
    credentials: 'include',
    ...init,
    headers,
  });

  if (!res.ok) {
    throw await apiRequestErrorFromResponse(res);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export async function apiRequestErrorFromResponse(
  res: Response,
  fallbackCode = 'UNKNOWN',
): Promise<ApiRequestError> {
  const body = (await res.json().catch(() => null)) as
    | { error?: { code?: string; message?: string; details?: unknown } }
    | null;
  return new ApiRequestError(
    res.status,
    body?.error?.code ?? fallbackCode,
    body?.error?.message ?? res.statusText,
    body?.error?.details,
  );
}

export type HealthResponse = healthContracts.HealthResponse;

export function getHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/api/health');
}
